// Growth tracking's calls, on v1 or on v2 by the doorway's `growth` area (src/lib/api/switch.ts, the flag
// `v2_room6_growth`; ai_app_backend's plan task growth-rewire): a visit's reports and a browser's link to its account
// (visits.js), an affiliate's click (hydrate-referral.js) and the upgrade page's checkout-offer events. Both APIs keep
// the same rows from the same reports; only the address, and for a click the field names, differ.
//
// The switch is decided once a page load (a visitor is placed by their visitor id, not their account) and loads only
// in a build with v2's address (`await import`), so a page's first load carries none of it. A switch that fails to
// load or answer sends everything to v1.

/** v1's API, and its payments host (where v1 keeps the checkout-offer event). */
const V1_API = process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD
const V1_PAYMENTS = process.env.NEXT_PUBLIC_PROMPTCHAN_PAYMENT

/** v2's API, when this build may call it: unset, everything goes to v1. */
const V2_API = process.env.NEXT_PUBLIC_AI_APP_API

let placed = null

/**
 * Whether this page load's growth tracking goes to v2: asked of the switch once, and kept until the page reloads.
 * `loadSwitch` is replaced only by tests.
 */
export function growthOnV2(loadSwitch = () => import('./api/switch.ts')) {
  if (!V2_API) return Promise.resolve(false)
  placed ??= loadSwitch()
    .then(({ onV2 }) => onV2('growth', ''))
    .catch(() => false)
  return placed
}

/** Forgets the page's placement, as a reload does (tests). */
export function forgetGrowthPlacement() {
  placed = null
}

/** Where a visit's report goes: v1's /api/nav or v2's "Record a visit". Both take the same text/plain body. */
export const visitsUrl = (onV2) =>
  onV2 ? `${V2_API}/v2/visits` : `${V1_API}/api/nav`

/**
 * Whether a visit report's answer means it is done with: kept, or refused for good (v1's 400, v2's 422 for a body that
 * isn't JSON, either's 413 for one too big). Anything else (a 429, a 5xx) is sent again.
 */
export const reportDone = (response) =>
  response.ok || [400, 413, 422].includes(response.status)

/** The request tying this browser to the signed-in account: v1's POST /api/nav/link, v2's PUT /v2/me/visitor. */
export const linkRequest = (onV2, token, body) => ({
  url: onV2 ? `${V2_API}/v2/me/visitor` : `${V1_API}/api/nav/link`,
  init: {
    method: onV2 ? 'PUT' : 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token },
    body: JSON.stringify(body),
  },
})

/** v1's record-event keys, by v2's "Record an affiliate's click" field each fills. */
const CLICK_FIELDS = {
  aiffiliateUid: 'affiliate_id',
  aiffiliateTracker: 'tracker',
  clickRef: 'click_ref',
  clickedAt: 'clicked_at',
  landing: 'landing',
  referrerHost: 'referrer_host',
  sub1: 'sub1',
  sub2: 'sub2',
  sub3: 'sub3',
  sub4: 'sub4',
  sub5: 'sub5',
}

/**
 * An affiliate's click report (hydrate-referral.js's `clickReport`), as the API it goes to takes it: v1's
 * /api/record-event takes it as JSON, which is preflighted; v2's POST /v2/affiliate-clicks takes v2's field names as
 * text/plain, read as JSON, which needs no preflight.
 */
export function clickRequest(onV2, body) {
  if (!onV2)
    return {
      url: `${V1_API}/api/record-event`,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  const sent = {}
  for (const [key, value] of Object.entries(body))
    if (Object.hasOwn(CLICK_FIELDS, key)) sent[CLICK_FIELDS[key]] = value
  return {
    url: `${V2_API}/v2/affiliate-clicks`,
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(sent),
  }
}

/** Where a checkout offer's event goes: v1's payments host, or v2's POST /v2/me/offer-events. The same JSON body. */
export const offerEventUrl = (onV2) =>
  onV2
    ? `${V2_API}/v2/me/offer-events`
    : `${V1_PAYMENTS}/api/checkout_offer_event`
